/**
 * Shared surface messages (whiteboard + annotate-over-share) over the
 * `ischool-surface` data-channel topic.
 *
 * Teacher-only mutations are enforced on both send and receive using the
 * JWT identity prefix (same pattern as celebrations in useClassroomSignals).
 */

export const SURFACE_TOPIC = 'ischool-surface'

/** Minimum gap between honouring two state requests from the same participant. */
export const REQUEST_COOLDOWN_MS = 3_000

/**
 * Budget for one reliable packet's user payload, in ENCODED BYTES.
 *
 * Bytes, not JS string length: an Arabic character costs two bytes in UTF-8, so
 * slicing a scene at 12,000 *characters* produced a ~24 KB packet the moment a
 * tutor typed Arabic on the board. That blows LiveKit's ~15 KiB reliable cap and
 * the publish fails, so the board stops syncing. Arabic is our default language,
 * so this was a matter of time rather than an edge case. (A failed chunk now
 * reaches the tutor as `boardError`; it used to vanish entirely.)
 *
 * Left well under the cap to cover the JSON envelope (kind/id/v/i/n/by) that
 * wraps every chunk body.
 */
export const CHUNK_BYTES = 10_000

/**
 * Refuse to publish a scene larger than this. A board that big is a runaway
 * (pasted content, thousands of elements) and chunking it would flood every
 * phone in the class; failing loudly to the tutor beats melting the room.
 */
export const MAX_SCENE_BYTES = 600_000

export type BoardOpenMessage = { kind: 'board_open'; v: number; by: string }
export type BoardCloseMessage = { kind: 'board_close'; v: number; by: string }
export type BoardClearMessage = { kind: 'board_clear'; v: number; by: string }
export type BoardRequestMessage = { kind: 'board_request' }
export type BoardChunkMessage = {
  kind: 'board_chunk'
  id: string
  v: number
  i: number
  n: number
  by: string
  body: string
}

export type InkTool = 'pen' | 'highlighter' | 'arrow' | 'rect' | 'ellipse' | 'line' | 'eraser'

/** Tools whose stroke is two points [start, end] rather than a freehand path. */
export const SHAPE_TOOLS: readonly InkTool[] = ['arrow', 'rect', 'ellipse', 'line']

export function isShapeTool(tool: InkTool): boolean {
  return SHAPE_TOOLS.includes(tool)
}

/** High-contrast over a dark IDE; the highlighter reuses these at low alpha. */
export const INK_COLORS = ['#ff4d4f', '#ffd60a', '#4ade80', '#38bdf8', '#ffffff'] as const
export type InkColor = (typeof INK_COLORS)[number]

export type InkPoint = { x: number; y: number }

export type InkStroke = {
  id: string
  /** Screen-share track SID — drops ink from a previous share. */
  shareId: string
  tool: InkTool
  color: string
  width: number
  points: InkPoint[]
  by: string
}

export type InkBeginMessage = {
  kind: 'ink_begin'
  id: string
  shareId: string
  tool: InkTool
  color: string
  width: number
  by: string
  /**
   * The stroke's origin, carried on the RELIABLE channel.
   *
   * It used to be published straight after this message as a separate lossy
   * `ink_point`. Lossy and reliable are different data channels with no ordering
   * between them, and a receiver drops a point for a stroke it has not seen yet
   * — so the seed that existed specifically to fix the wrong-origin bug was
   * itself lost whenever it overtook its own `ink_begin` (or the network simply
   * dropped it). Carrying the origin here removes that race for any receiver
   * that understands the field.
   *
   * Optional only for mixed-version RECEIVE: a class can hold clients loaded
   * before this shipped. Always set it when sending.
   */
  at?: InkPoint
}
export type InkPointMessage = {
  kind: 'ink_point'
  id: string
  shareId: string
  x: number
  y: number
}
export type InkEndMessage = {
  kind: 'ink_end'
  id: string
  shareId: string
  points: InkPoint[]
  tool: InkTool
  color: string
  width: number
  by: string
}
export type InkClearMessage = { kind: 'ink_clear'; shareId: string; by: string }
export type InkUndoMessage = { kind: 'ink_undo'; id: string; shareId: string; by: string }
export type InkRequestMessage = { kind: 'ink_request'; shareId?: string }
export type InkSnapshotMessage = {
  kind: 'ink_snapshot'
  shareId: string
  strokes: InkStroke[]
  by: string
}
/** Arm/disarm remote ink UI when tutor toggles annotate mode. */
export type InkArmMessage = { kind: 'ink_arm'; armed: boolean; shareId?: string; by: string }

export type SurfaceMessage =
  | BoardOpenMessage
  | BoardCloseMessage
  | BoardClearMessage
  | BoardRequestMessage
  | BoardChunkMessage
  | InkBeginMessage
  | InkPointMessage
  | InkEndMessage
  | InkClearMessage
  | InkUndoMessage
  | InkRequestMessage
  | InkSnapshotMessage
  | InkArmMessage

export type BoardScenePayload = {
  elements: readonly unknown[]
  appState?: { viewBackgroundColor?: string }
}
